import React from 'react';

import { cn } from '../../../../lib/utils';
import { MemberDays, TeamMember, WORK_DAY_HOURS } from '../../../../model/team.model';
import { formatHours } from '../teamMock';

const DAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Five steps is enough to read a pattern; more just looks noisy. */
const intensity = (hours: number) => {
  if (hours <= 2) return 0.22;
  if (hours <= 4) return 0.42;
  if (hours <= 6) return 0.62;
  if (hours <= WORK_DAY_HOURS) return 0.82;
  return 1;
};

interface ActivityHeatmapProps {
  members: TeamMember[];
  days: Date[];
  /** Hours per member, already sliced to the visible window. */
  hoursByMember: Record<string, number[]>;
  isWeekend: (index: number) => boolean;
  todayIndex: number;
  selectedMemberId: string | null;
  onSelectMember: (id: string | null) => void;
}

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({
  members,
  days,
  hoursByMember,
  isWeekend,
  todayIndex,
  selectedMemberId,
  onSelectMember,
}) => (
  <div className="overflow-x-auto">
    <table className="border-separate border-spacing-0">
      <thead>
        <tr>
          <th className="sticky left-0 z-10 bg-card pr-4" />
          {days.map((date, index) => (
            <th
              key={index}
              className={cn(
                'w-7 pb-1 text-center text-[10px] font-normal',
                index === todayIndex ? 'text-foreground' : 'text-muted-foreground/60',
              )}
            >
              {DAY_INITIALS[index % 7]}
            </th>
          ))}
          <th className="w-20 pb-1 pl-4 text-right text-[10px] font-normal uppercase tracking-wide text-muted-foreground">
            Logged
          </th>
        </tr>
      </thead>
      <tbody>
        {members.map(member => {
          const hours = hoursByMember[member.id] ?? [];
          const total = hours.reduce((acc, value) => acc + value, 0);
          const isSelected = selectedMemberId === member.id;
          const silent = total === 0;

          return (
            <tr key={member.id}>
              <td className="sticky left-0 z-10 bg-card py-0.5 pr-4">
                <button
                  type="button"
                  onClick={() => onSelectMember(isSelected ? null : member.id)}
                  className={cn(
                    'flex w-full items-center gap-2 whitespace-nowrap rounded px-1.5 py-1 text-left text-sm transition-colors',
                    isSelected ? 'bg-accent text-foreground' : 'hover:bg-muted',
                  )}
                >
                  <span className={cn('truncate', silent && 'text-muted-foreground')}>{member.name}</span>
                  {silent && (
                    <span className="shrink-0 rounded border border-amber-500/40 px-1 text-[10px] text-amber-600 dark:text-amber-500">
                      no data
                    </span>
                  )}
                </button>
              </td>

              {hours.map((value, index) => {
                const weekend = isWeekend(index);
                const title = `${member.name} · ${days[index].getDate()}: ${formatHours(value)} h`;

                if (value > 0) {
                  return (
                    <td key={index} className="px-[2px] py-[2px]">
                      <div
                        title={title}
                        className="h-6 w-6 rounded-sm bg-foreground"
                        style={{ opacity: intensity(value) }}
                      />
                    </td>
                  );
                }

                return (
                  <td key={index} className="px-[2px] py-[2px]">
                    <div
                      title={weekend ? undefined : `${member.name}: nothing logged`}
                      className={cn(
                        'h-6 w-6 rounded-sm',
                        weekend ? 'bg-muted/50' : 'border border-dashed border-amber-500/50',
                      )}
                    />
                  </td>
                );
              })}

              <td className="pl-4 text-right text-sm tabular-nums">
                {total > 0 ? formatHours(total) : <span className="text-muted-foreground/40">·</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default ActivityHeatmap;
